import { type ReactNode } from 'react';
import {
  BatteryLow,
  BatteryMedium,
  Clock,
  Cpu,
  HardDrive,
  Monitor,
  Thermometer,
  Timer,
  Wrench,
} from 'lucide-react';
import { cn } from '@staff-app/ui';
import { formatDateMedium } from '@/lib/format-date';
import { safeDistanceToNow } from '@/lib/safe-format';
import type { MdmLiveInfoStatus } from '../../hooks/useTestingMdmLiveInfo';
import { formatBytes, formatUptimeDuration, simplifyRatio } from './lib/device-metrics';
import type { DeviceLiveInfo } from './lib/parse-device-info';

const HOT_TEMP_C = 70;
const LOW_BATTERY_PCT = 20;
const USAGE_WARN_PCT = 85;

/** Colorblind-safe "this value came live from the device just now" marker — teal + a filled dot +
 *  text (never green-only). scio3 parity. */
function LiveMarker() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-teal-600 dark:text-teal-400">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-teal-500" aria-hidden />
      {'Live'}
    </span>
  );
}

/**
 * scio3's resolution inset: the live width × height centered in a soft primary-tint block, ratio +
 * refresh rate beneath — the card grid's one color accent, pinned to the card base.
 */
function ResolutionInset({
  width,
  height,
  sub,
}: {
  width: string;
  height: string;
  sub: string | null;
}) {
  return (
    <div
      className="rounded-sm bg-primary/5 px-2 py-1.5 text-center"
      data-testid="testing-display-inset"
    >
      <div className="text-sm font-semibold tabular-nums text-foreground">
        {width} × {height}
      </div>
      {sub ? <div className="text-[10px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function UsageBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <svg
      width="100%"
      height="6"
      viewBox="0 0 100 6"
      preserveAspectRatio="none"
      role="img"
      aria-label={`${clamped}%`}
    >
      <rect x="0" y="0" width="100" height="6" rx="3" className="fill-border" />
      <rect
        x="0"
        y="0"
        width={Math.max(3, clamped)}
        height="6"
        rx="3"
        className={clamped > USAGE_WARN_PCT ? 'fill-warning' : 'fill-primary'}
      />
    </svg>
  );
}

function StateCard({
  icon,
  label,
  value,
  sub,
  pending,
  bottom,
  live,
  updating,
  loading,
  testId,
}: {
  icon: ReactNode;
  label: string;
  value?: ReactNode;
  sub?: ReactNode;
  pending?: boolean;
  bottom?: ReactNode;
  live?: boolean;
  /** The live pull is in flight — a quiet "Updating…" on this card. */
  updating?: boolean;
  /** Live pull in flight and nothing to show yet — shimmer instead of a premature N/A. */
  loading?: boolean;
  testId?: string;
}) {
  return (
    <div className="flex min-h-28 flex-col rounded-md bg-muted/50 p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-1.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          {icon}
          {label}
        </span>
        {live ? <LiveMarker /> : null}
        {updating && !live && !loading ? (
          <span className="text-[10px] text-muted-foreground">{'Updating…'}</span>
        ) : null}
      </div>
      {loading ? (
        <>
          <div className="mt-1.5 h-7 w-20 animate-pulse rounded bg-muted" />
          <div className="mt-1 h-3 w-14 animate-pulse rounded bg-muted" />
        </>
      ) : (
        <>
          {value != null ? (
            <div
              className={cn(
                'mt-1.5 text-xl font-semibold tabular-nums',
                pending ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {value}
            </div>
          ) : null}
          {sub ? <div className="text-[11px] text-muted-foreground">{sub}</div> : null}
        </>
      )}
      {bottom ? <div className="mt-auto pt-2">{bottom}</div> : null}
    </div>
  );
}

function usagePct(
  liveUsage: string | undefined,
  liveTotal: string | undefined,
  rawUsed: number | null | undefined,
  rawTotal: number | null | undefined,
): number | null {
  if (liveUsage && liveTotal) {
    const used = parseFloat(liveUsage);
    const total = parseFloat(liveTotal);
    if (Number.isFinite(used) && Number.isFinite(total) && total > 0) {
      return Math.min(100, Math.round((used / total) * 100));
    }
  }
  if (rawUsed && rawTotal && rawTotal > 0)
    return Math.min(100, Math.round((rawUsed / rawTotal) * 100));
  return null;
}

/** Why there is no live pull, in the operator's terms. */
function skippedReason(cap: string | null | undefined): string {
  if (cap === 'utils-mdm') return 'The management agent is offline — showing last known state.';
  if (cap === 'builtin-mdm') return 'The app is offline — showing last known state.';
  if (cap === 'platform-api') return "Live device state isn't available for Room systems.";
  return "Live device state isn't available for Content-only devices — showing last known state.";
}

export interface DeviceStateSnapshot {
  usedStorage?: number | null;
  totalStorage?: number | null;
  activationDate?: string | null;
  tz?: string | null;
}

/**
 * Unified device-state card grid — port of scio3 `DeviceStateCards`. The live `GET_DEVICE_INFO`
 * pull (see useTestingMdmLiveInfo) overlays fresh values with a "Live" marker; snapshot values from the
 * device record show first, and are all there is when the pull is skipped or the device does not
 * answer. Presentational: the drawer owns the hook so the gate reads the same connectivity it shows.
 */
export function DeviceStateCards({
  info,
  status,
  snapshot,
  managementCapability,
}: {
  info: DeviceLiveInfo | null;
  status: MdmLiveInfoStatus;
  snapshot: DeviceStateSnapshot;
  managementCapability: string | null | undefined;
}) {
  const isUpdating = status === 'loading';

  const snapUsed = formatBytes(snapshot.usedStorage);
  const snapTotal = formatBytes(snapshot.totalStorage);
  const storageUsed = info?.storageUsage ?? snapUsed;
  const storageTotal = info?.storageTotal ?? snapTotal;
  const storageValue = storageUsed ?? storageTotal ?? '—';
  const storageSub = storageUsed && storageTotal ? `of ${storageTotal}` : undefined;
  const storagePct = usagePct(
    info?.storageUsage,
    info?.storageTotal,
    snapshot.usedStorage,
    snapshot.totalStorage,
  );

  const memUsed = info?.ramUsage ?? null;
  const memTotal = info?.ramTotal ?? null;
  const memValue = memUsed ?? memTotal;
  const memSub = memUsed && memTotal ? `of ${memTotal}` : undefined;
  const memPct = usagePct(info?.ramUsage, info?.ramTotal, undefined, undefined);

  const liveDisplay = Boolean(info?.displayWidth && info?.displayHeight);
  const ratioLabel = simplifyRatio(Number(info?.displayWidth), Number(info?.displayHeight));

  const uptimeValue = formatUptimeDuration(info?.uptimeSeconds);
  const bootedAt = info?.uptimeSeconds
    ? formatDateMedium(new Date(Date.now() - info.uptimeSeconds * 1000), '')
    : '';

  const tempC = info?.temperature ? parseInt(info.temperature, 10) : null;
  const runsHot = tempC != null && Number.isFinite(tempC) && tempC >= HOT_TEMP_C;

  const batteryPercent = info?.batteryPercent;
  const hasBattery = batteryPercent != null;
  const batteryLow = hasBattery && batteryPercent <= LOW_BATTERY_PCT;

  const activeSince = snapshot.activationDate
    ? safeDistanceToNow(snapshot.activationDate, {}, '—')
    : '—';

  let localTime = '';
  if (snapshot.tz) {
    try {
      localTime = new Intl.DateTimeFormat('en-US', {
        timeZone: snapshot.tz,
        hour: 'numeric',
        minute: '2-digit',
      }).format(Date.now());
    } catch {
      localTime = '';
    }
  }

  const na = (
    <span className="inline-flex items-center gap-1">
      <Wrench className="h-3 w-3" aria-hidden />
      {status === 'live' ? 'Needs firmware' : 'Not reported'}
    </span>
  );
  const displaySub = liveDisplay
    ? [ratioLabel, info?.refreshRate ? `${info.refreshRate} Hz` : null].filter(Boolean).join(' · ')
    : null;

  return (
    <div className="space-y-2">
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3"
        data-testid="testing-device-state-cards"
      >
        <StateCard
          testId="testing-state-storage"
          icon={<HardDrive className="h-3.5 w-3.5" aria-hidden />}
          label={'Storage'}
          value={storageValue}
          sub={storageSub}
          bottom={storagePct != null ? <UsageBar pct={storagePct} /> : undefined}
          live={!!info?.storageUsage || !!info?.storageTotal}
          updating={isUpdating}
          loading={isUpdating && !storageUsed && !storageTotal}
        />
        <StateCard
          testId="testing-state-memory"
          icon={<Cpu className="h-3.5 w-3.5" aria-hidden />}
          label={'Memory'}
          value={memValue ?? 'N/A'}
          sub={memValue ? memSub : na}
          pending={!memValue}
          bottom={memPct != null ? <UsageBar pct={memPct} /> : undefined}
          live={!!memValue}
          updating={isUpdating}
          loading={isUpdating && !memValue}
        />
        <StateCard
          testId="testing-state-display"
          icon={<Monitor className="h-3.5 w-3.5" aria-hidden />}
          label={'Display output'}
          value={liveDisplay ? undefined : 'N/A'}
          sub={liveDisplay ? undefined : na}
          bottom={
            liveDisplay ? (
              <ResolutionInset
                width={String(info?.displayWidth)}
                height={String(info?.displayHeight)}
                sub={displaySub || null}
              />
            ) : undefined
          }
          pending={!liveDisplay}
          live={liveDisplay}
          updating={isUpdating}
          loading={isUpdating && !liveDisplay}
        />
        <StateCard
          testId="testing-state-uptime"
          icon={<Timer className="h-3.5 w-3.5" aria-hidden />}
          label={'Uptime'}
          value={uptimeValue ?? 'N/A'}
          sub={uptimeValue ? (bootedAt ? `since ${bootedAt}` : 'since last boot') : na}
          pending={!uptimeValue}
          live={!!uptimeValue}
          updating={isUpdating}
          loading={isUpdating && !uptimeValue}
        />
        <StateCard
          icon={<Clock className="h-3.5 w-3.5" aria-hidden />}
          label={'Active since'}
          value={activeSince === '—' ? '—' : `${activeSince} ago`}
        />
        <StateCard
          icon={<Clock className="h-3.5 w-3.5" aria-hidden />}
          label={'Time zone'}
          value={snapshot.tz ?? '—'}
          sub={localTime ? `${localTime} local` : undefined}
        />
        <StateCard
          testId="testing-state-temperature"
          icon={<Thermometer className="h-3.5 w-3.5" aria-hidden />}
          label={'Temperature'}
          value={
            info?.temperature ? (
              <span className={cn(runsHot && 'text-warning')}>{info.temperature}</span>
            ) : (
              'N/A'
            )
          }
          sub={info?.temperature ? (runsHot ? 'runs hot' : undefined) : na}
          pending={!info?.temperature}
          live={!!info?.temperature}
          updating={isUpdating}
          loading={isUpdating && !info?.temperature}
        />
        {hasBattery && (
          <StateCard
            icon={
              batteryLow ? (
                <BatteryLow className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <BatteryMedium className="h-3.5 w-3.5" aria-hidden />
              )
            }
            label={'Battery'}
            value={<span className={cn(batteryLow && 'text-warning')}>{`${batteryPercent}%`}</span>}
            sub={batteryLow ? 'runs low' : undefined}
            live
          />
        )}
      </div>

      {status === 'live' ? (
        <div className="text-[11px] text-muted-foreground" data-testid="testing-live-info-fresh">
          {'Live device state pulled just now.'}
        </div>
      ) : null}
      {status === 'no-response' ? (
        <div
          className="text-[11px] text-muted-foreground"
          data-testid="testing-live-info-no-response"
        >
          {"Couldn't reach the device just now — showing last known state."}
        </div>
      ) : null}
      {status === 'skipped' ? (
        <div className="text-[11px] text-muted-foreground" data-testid="testing-live-info-skipped">
          {skippedReason(managementCapability)}
        </div>
      ) : null}
    </div>
  );
}
